import { ApiErrorResponse } from 'apisauce';
import * as R from 'ramda';
import { ErrorResponse } from '../../../lib/basicRepository';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import { GridItemSkuDTO } from '../../products/details/productDetails.types';
import { onlineKitActionRepository } from '../../products/repositories/productActions/onlineKitActions.repository';
import { ProductActionsResponseData } from '../../products/repositories/types/productActions.types';
import { offlineKitsRepository } from '../repositories/offlineKits.repository';
import { offlineOrdersRepository } from '../repositories/offlineOrders.repository';
import { useOfflineOrdersStore } from '../stores/offlineOrders/offlineOrders.store';
import { CreateGridParams } from '../stores/offlineOrders/types/offlineOrders.actions.types';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';
import { createCustomerUnauthorizedKitError } from './utils/createCustomerUnauthorizedError.utils';
import { mountGridItemFromSkuKit } from './utils/kitActions.utils';
import { validateKitHasConflictsOnOrderProductsAndReturnError } from './utils/onlineOrderRepository.utils';
import { ValidateCatalogSectionItemForCustomerCatalogsUtils } from './utils/validateCatalogSectionItemForCustomerCatalogs.utils';

const kitActionsRepositoryOfflineAdapters: typeof onlineKitActionRepository = {
  unshopKit: async ({ kitId, orderId }) => {
    const kit = await offlineKitsRepository.getByIdOrFail(kitId);

    const kitProductColorIds = kit.skuKits.map((skuKit) => skuKit.productColor.id);
    const uniqueProductColorIds = R.uniq(kitProductColorIds);

    await useOfflineOrdersStore
      .getState()
      .removeAllOrderProductsFromProductColors(orderId, uniqueProductColorIds);

    return createApiSauceResponse<ProductActionsResponseData, ErrorResponse>({
      ok: true,
      data: {
        isShopping: false,
        productColorId: '',
        productId: '',
        productCode: '',
        productName: '',
      },
    });
  },
  shopKit: async ({ kitId, orderId }) => {
    const kit = await offlineKitsRepository.getByIdOrFail(kitId);
    const order = await offlineOrdersRepository.getByIdOrThrow(orderId);
    const brandGeneralSettings = useGeneralSettingsStore.getState().generalSettings;
    const restrictProductsUsingCustomerCatalogs =
      !!brandGeneralSettings.restrictProductsUsingCustomerCatalogs;

    const customerHasAccessToProductColor =
      await ValidateCatalogSectionItemForCustomerCatalogsUtils.validateForKit({
        customerId: order.customer?.id!,
        kitId,
        restrictProductsUsingCustomerCatalogs,
      });

    if (!customerHasAccessToProductColor) {
      return createCustomerUnauthorizedKitError();
    }

    const offlineOrder = await offlineOrdersRepository.getById(orderId);
    if (offlineOrder?.products && offlineOrder.products.length > 0) {
      const orderProductCombinationsMap = offlineOrder.getOrderProductCombinationsMap();

      const error = validateKitHasConflictsOnOrderProductsAndReturnError({
        orderProductCombinationsMap,
        kitId,
        skuKits: kit.skuKits,
      });

      if (error) {
        return error as ApiErrorResponse<ErrorResponse>;
      }
    }

    const gridItems: CreateGridParams[] = kit.skuKits.map((skuKit) => {
      const grid = mountGridItemFromSkuKit(skuKit);

      return grid;
    });

    await useOfflineOrdersStore.getState().addKitProductsBasedOnGrid({ orderId, gridItems, kitId });

    return createApiSauceResponse<GridItemSkuDTO[], ErrorResponse>({
      ok: true,
      data: [],
    });
  },
};

export default kitActionsRepositoryOfflineAdapters;
